import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AssistedTransactionCategory, DistrictOffice, OfficeStats, StaffPosition } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

const ASSISTED_CATEGORIES: AssistedTransactionCategory[] = [
  'LOCAL_ISSUE',
  'FACILITY_BOOKING',
  'COMMUNITY_PROJECT',
  'NOTICE_BOARD',
  'GENERAL_ENQUIRY',
  'OTHER',
];

const STAFF_POSITIONS: StaffPosition[] = ['DISTRICT_ADMINISTRATOR', 'DA_OFFICER'];

const DAYS: Array<{ key: string; label: string }> = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

/**
 * District office dashboard — GET /offices/:district (office + staff roster,
 * since the signed-in persona is that office's own active staff) and
 * GET /offices/:district/stats (the "assisted-service stats to C14" hook:
 * total, last30Days, byCategory, computed live from AssistedTransaction
 * rows — no placeholders). Every call below is hard-bound to the signed-in
 * persona's own district; there is no picker (see ui/Shell.tsx).
 */
export function Dashboard() {
  const { persona } = useAuth();
  const district = persona?.district ?? '';
  const isAdmin = persona?.roles.includes('DISTRICT_ADMINISTRATOR') ?? false;
  const encoded = encodeURIComponent(district);

  const officeQ = useApiQuery<DistrictOffice>(district ? `/api/v1/m41/offices/${encoded}` : null);
  const statsQ = useApiQuery<OfficeStats>(district ? `/api/v1/m41/offices/${encoded}/stats` : null);

  const [showLog, setShowLog] = useState(false);
  const [showEditOffice, setShowEditOffice] = useState(false);
  const [showAppoint, setShowAppoint] = useState(false);
  const [deactivating, setDeactivating] = useState<string | null>(null);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const deactivate = async (staffId: string) => {
    setDeactivating(staffId);
    setDeactivateError(null);
    try {
      await api.post(`/api/v1/m41/offices/${encoded}/staff/${staffId}/deactivate`);
      officeQ.reload();
    } catch (e) {
      setDeactivateError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to deactivate staff member');
    } finally {
      setDeactivating(null);
    }
  };

  if (!district) {
    return <ErrorNote message="This account has no district binding on file — see server/staff-access.ts." />;
  }

  return (
    <div>
      <PageHeader
        title={`${district} District Office`}
        subtitle="Real assisted-transaction stats computed from this district's own log."
        actions={
          <>
            <button className="btn-secondary" onClick={() => setShowLog(true)}>
              + Log assisted transaction
            </button>
            {isAdmin ? (
              <button className="btn-secondary" onClick={() => setShowEditOffice(true)}>
                Edit office
              </button>
            ) : null}
            {isAdmin ? (
              <button className="btn-primary" onClick={() => setShowAppoint(true)}>
                + Appoint staff
              </button>
            ) : null}
          </>
        }
      />

      {officeQ.error ? <ErrorNote message={officeQ.error} /> : null}
      {officeQ.loading ? <Spinner label="Loading office…" /> : null}

      {officeQ.data ? (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-3">
            {statsQ.loading ? (
              <div className="sm:col-span-3">
                <Spinner label="Computing stats…" />
              </div>
            ) : statsQ.error ? (
              <div className="sm:col-span-3">
                <ErrorNote message={statsQ.error} />
              </div>
            ) : statsQ.data ? (
              <>
                <StatCard label="Total assisted transactions" value={statsQ.data.totalAssistedTransactions} />
                <StatCard label="Last 30 days" value={statsQ.data.last30Days} />
                <StatCard
                  label="By category"
                  value={Object.keys(statsQ.data.byCategory).length}
                  hint={
                    Object.entries(statsQ.data.byCategory)
                      .map(([k, v]) => `${k.replaceAll('_', ' ')}: ${v}`)
                      .join(' · ') || 'None logged yet'
                  }
                />
              </>
            ) : null}
          </div>

          <div className="mb-6 grid gap-5 lg:grid-cols-2">
            <div className="card p-5">
              <h2 className="mb-2 text-sm font-bold text-slate-700">Office details</h2>
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-500">Code</dt>
                  <dd className="font-medium text-slate-800">{officeQ.data.code}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Island</dt>
                  <dd className="font-medium text-slate-800">{officeQ.data.island}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Phone</dt>
                  <dd className="font-medium text-slate-800">{officeQ.data.contactPhone ?? '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Email</dt>
                  <dd className="font-medium text-slate-800">{officeQ.data.contactEmail ?? '—'}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="shrink-0 text-slate-500">Services offered</dt>
                  <dd className="text-right font-medium text-slate-800">{officeQ.data.servicesOffered.join(', ') || '—'}</dd>
                </div>
              </dl>
              <div className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-500">
                <div className="mb-1 font-semibold uppercase tracking-wide text-slate-400">Opening hours</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {DAYS.map((d) => (
                    <div key={d.key} className="flex justify-between">
                      <span className="uppercase">{d.label}</span>
                      <span className="tabular-nums">{officeQ.data!.hours?.[d.key] ?? 'closed'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card p-5">
              <h2 className="mb-2 text-sm font-bold text-slate-700">Staff roster</h2>
              {deactivateError ? (
                <div className="mb-2">
                  <ErrorNote message={deactivateError} />
                </div>
              ) : null}
              {(officeQ.data.staff?.length ?? 0) === 0 ? (
                <EmptyState title="No staff on file" hint="Not visible unless the signed-in persona is this office's own active staff." />
              ) : (
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="table-head">Person ID (NIN)</th>
                      <th className="table-head">Position</th>
                      <th className="table-head">Status</th>
                      {isAdmin ? <th className="table-head text-right">Actions</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {officeQ.data.staff!.map((s) => (
                      <tr key={s.id} className="hover:bg-slate-50">
                        <td className="table-cell font-mono text-xs">{s.personId}</td>
                        <td className="table-cell">{s.title ?? s.position.replaceAll('_', ' ')}</td>
                        <td className="table-cell">
                          <StatusBadge status={s.active ? 'ACTIVE' : 'CLOSED'} />
                        </td>
                        {isAdmin ? (
                          <td className="table-cell text-right">
                            {s.active ? (
                              <button
                                className="text-xs font-semibold text-rose-600 hover:underline disabled:opacity-50"
                                disabled={deactivating === s.id}
                                onClick={() => deactivate(s.id)}
                              >
                                {deactivating === s.id ? 'Deactivating…' : 'Deactivate'}
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      ) : null}

      {showLog ? (
        <LogTransactionModal
          district={district}
          onClose={() => setShowLog(false)}
          onDone={() => {
            setShowLog(false);
            statsQ.reload();
          }}
        />
      ) : null}
      {showEditOffice && officeQ.data ? (
        <EditOfficeModal
          office={officeQ.data}
          onClose={() => setShowEditOffice(false)}
          onDone={() => {
            setShowEditOffice(false);
            officeQ.reload();
          }}
        />
      ) : null}
      {showAppoint ? (
        <AppointStaffModal
          district={district}
          onClose={() => setShowAppoint(false)}
          onDone={() => {
            setShowAppoint(false);
            officeQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function LogTransactionModal({ district, onClose, onDone }: { district: string; onClose: () => void; onDone: () => void }) {
  const [category, setCategory] = useState<AssistedTransactionCategory>('GENERAL_ENQUIRY');
  const [citizenId, setCitizenId] = useState('');
  const [refType, setRefType] = useState('');
  const [refId, setRefId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m41/offices/${encodeURIComponent(district)}/assisted-transactions`, {
        category,
        citizenId: citizenId.trim() || undefined,
        refType: refType.trim() || undefined,
        refId: refId.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to log transaction');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log assisted transaction" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Category">
          <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as AssistedTransactionCategory)}>
            {ASSISTED_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Citizen NIN (optional)">
          <input className="field-input" value={citizenId} onChange={(e) => setCitizenId(e.target.value)} placeholder="e.g. SC901122007" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Reference type (optional)">
            <input className="field-input" value={refType} onChange={(e) => setRefType(e.target.value)} placeholder="e.g. FacilityBooking" />
          </Field>
          <Field label="Reference id (optional)">
            <input className="field-input" value={refId} onChange={(e) => setRefId(e.target.value)} placeholder="e.g. FB-2026-000123" />
          </Field>
        </div>
        <Field label="Notes (optional)">
          <textarea className="field-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Logging…' : 'Log transaction'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EditOfficeModal({ office, onClose, onDone }: { office: DistrictOffice; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(office.name);
  const [contactPhone, setContactPhone] = useState(office.contactPhone ?? '');
  const [contactEmail, setContactEmail] = useState(office.contactEmail ?? '');
  const [servicesOffered, setServicesOffered] = useState(office.servicesOffered.join(', '));
  const [hours, setHours] = useState<Record<string, string>>(office.hours ?? {});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const hoursPayload = Object.fromEntries(Object.entries(hours).filter(([, v]) => v.trim().length > 0));
      await api.patch(`/api/v1/m41/offices/${encodeURIComponent(office.district)}`, {
        name: name.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        servicesOffered: servicesOffered
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        hours: hoursPayload,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to update office');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Edit ${office.district} office`} onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact phone">
            <input className="field-input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          </Field>
          <Field label="Contact email">
            <input className="field-input" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          </Field>
        </div>
        <Field label="Services offered (comma separated)">
          <input className="field-input" value={servicesOffered} onChange={(e) => setServicesOffered(e.target.value)} />
        </Field>
        <Field label="Opening hours (blank = closed)">
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
            {DAYS.map((d) => (
              <div key={d.key}>
                <div className="mb-0.5 text-center text-[10px] font-semibold uppercase text-slate-400">{d.label}</div>
                <input
                  className="field-input !px-1.5 !py-1 text-center text-xs"
                  value={hours[d.key] ?? ''}
                  onChange={(e) => setHours((h) => ({ ...h, [d.key]: e.target.value }))}
                  placeholder="closed"
                />
              </div>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Format HH:MM-HH:MM, e.g. 08:00-16:00.</p>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || name.trim().length < 2}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AppointStaffModal({ district, onClose, onDone }: { district: string; onClose: () => void; onDone: () => void }) {
  const [personId, setPersonId] = useState('');
  const [position, setPosition] = useState<StaffPosition>('DA_OFFICER');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m41/offices/${encodeURIComponent(district)}/staff`, {
        personId: personId.trim(),
        position,
        title: title.trim() || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to appoint staff member');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Appoint staff member" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Person ID (NIN)">
          <input className="field-input" value={personId} onChange={(e) => setPersonId(e.target.value)} placeholder="e.g. SC901122007" />
        </Field>
        <Field label="Position">
          <select className="field-input" value={position} onChange={(e) => setPosition(e.target.value as StaffPosition)}>
            {STAFF_POSITIONS.map((p) => (
              <option key={p} value={p}>
                {p.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Title (optional)">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. DA Officer — Frontline Services" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || personId.trim().length < 3}>
            {busy ? 'Appointing…' : 'Appoint'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
